import './App.scss'; // this needs to be the first import
import '@healthiq/vantage-ui-components/dist/index.css';
import React, {useEffect, useRef, useState} from 'react';
import { Redirect,Route, Switch, useHistory, useLocation } from "react-router-dom";
import {getICD10Tree, getICD10IPDNTree, getProcedureTree, getSettings, getProcedureIPDNTree} from '../actions';
import { getNotifications } from "../actions/actions";
import { fetchPermissions, fetchResources, fetchUser } from "../actions/userSlice";
import { fetchWorkbooks } from '../containers/Storyboard/storyboardSlice';
import { useAppDispatch, useAppSelector } from "../hooks";
import { GlossaryPage } from "../pages/GlossaryPage";
import { HealthCareUsageReportPage } from "../pages/HealthCareUsageReportPage";
import { HealthCareUsageReportStudiesPage } from "../pages/HealthCareUsageReportStudiesPage";
import { HomePage } from "../pages/homepage/HomePage";
import MyAccountPage from "../pages/MyAccountPage";
import { PathwayViewerPage } from "../pages/PathwayViewerPage";
import { PathwayViewerStudiesPage } from "../pages/PathwayViewerStudiesPage";
import {COHORT_ID, Routes} from "../pages/routes";
import { SimulationsPage } from "../pages/SimulationsPage";
import { StoryboardPage } from "../pages/StoryboardPage";
import { ProjectsPage } from "../pages/ProjectsPage";
import { StudyPage } from "../pages/StudyPage";
import { TrainingPage } from "../pages/TrainingPage";
import { DATA_RANGE_IP, selectValues } from '../reducers/settings';
import SettingsContext from '../reducers/SettingsContext';
import usePageTracking from "../util/pageTracking";
import {COHORT_ANALYSER_TITLE, QueryBuilderPage} from '../pages/QueryBuilderPage';
import { fetchFields } from '../containers/QueryBuilder/queryBuilderSlice';
import { QueryBuilderHistoryPage } from '../pages/QueryBuilderHistoryPage';
import { QueryBuilderProgressPage } from '../pages/QueryBuilderProgressPage';
import {VisualisationsPage} from "../pages/VisualisationsPage";
import {ErrorPage} from "./ErrorPage";
import LoginPage from "../pages/loginpage/LoginPage";

const PageContent = ({isLoggedIn, setLoggedIn} : {isLoggedIn: boolean, setLoggedIn: (loggedIn: boolean) => void}) => {
  if (!isLoggedIn) {
    return <LoginPage setLoggedIn={setLoggedIn}/>
  }

  return <Switch>
    <Route path={Routes.Projects}>
      <ProjectsPage/>
    </Route>
    <Route path={Routes.Project}>
      <StudyPage/>
    </Route>
    <Route path={Routes.MyStudiesHRU}>
      <HealthCareUsageReportStudiesPage/>
    </Route>
    <Route path={Routes.MyStudiesPWV}>
      <PathwayViewerStudiesPage/>
    </Route>
    <Route path={Routes.MyHRU}>
      <HealthCareUsageReportPage/>
    </Route>
    <Route path={Routes.MyPWV}>
      <PathwayViewerPage/>
    </Route>
    <Route path={Routes.Training}>
      <TrainingPage/>
    </Route>
    <Route path={Routes.Glossary}>
      <GlossaryPage/>
    </Route>
    <Route path={`${Routes.Storyboard}/:workbookId`}>
      <StoryboardPage/>
    </Route>
    <Route path={Routes.Storyboard}>
      <StoryboardPage/>
    </Route>
    <Route path={Routes.Home}>
      <HomePage/>
    </Route>
    <Route path={Routes.MyAccount}>
      <MyAccountPage/>
    </Route>
    <Route path={`${Routes.Simulations}/:simulationCode`}>
      <SimulationsPage/>
    </Route>
    <Route path={Routes.RetinaMarketTrends}>
      <StoryboardPage showHeader={false} title='Retina Market Trends' tag={Routes.RetinaMarketTrends.substring(1)} />
    </Route>
    <Route path={Routes.VestrumPracticePerformanceTracker}>
      <StoryboardPage showHeader={false} title='Vestrum Practice Performance Tracker' tag={Routes.VestrumPracticePerformanceTracker.substring(1)} />
    </Route>
    <Route path={Routes.Visualisations}>
      <VisualisationsPage/>
    </Route>
    <Route path={Routes.QueryBuilderV2}>
      <QueryBuilderPage />
    </Route>
    <Route path={Routes.QueryBuilderHistory}>
      <QueryBuilderHistoryPage isCohortAnalyser={false}/>
    </Route>
    <Route path={Routes.QueryBuilderProgress}>
      <QueryBuilderProgressPage isCohortAnalyser={false} />
    </Route>
    {/* needs to remain a path param as the current version of spring doesn't redirect with query params */}
    <Route path={`${Routes.CohortAnalyser}/:${COHORT_ID}`}>
      <QueryBuilderPage title={COHORT_ANALYSER_TITLE} />
    </Route>
    <Route path={Routes.CohortAnalyser}>
      <QueryBuilderPage title={COHORT_ANALYSER_TITLE} />
    </Route>
    <Route path={Routes.CohortAnalyserHistory}>
      <QueryBuilderHistoryPage isCohortAnalyser={true}/>
    </Route>
    <Route path={Routes.CohortAnalyserProgress}>
      <QueryBuilderProgressPage isCohortAnalyser={true}/>
    </Route>
    <Route path={`/error/:errorCode?`}>
      <ErrorPage/>
    </Route>
    <Route path={Routes.Login}>
      <Redirect to={Routes.Home}/>
    </Route>
    <Route path="*">
      <Redirect to={Routes.Home}/>
    </Route>
  </Switch>
}

const App = () => {

  const [loggedIn, setLoggedIn] = useState(false)
  const initialised = useRef(false)


  const dispatch = useRef(useAppDispatch());
  const location = useRef(useLocation());
  const history = useRef(useHistory());
  usePageTracking()

  useEffect(() => {
    if (loggedIn && !initialised.current) {
      initialised.current = true

      const searchParams = new URLSearchParams(location.current.search)
      dispatch.current(fetchUser())
      dispatch.current(getNotifications())
      dispatch.current(fetchPermissions())
      dispatch.current(fetchResources())
      dispatch.current(fetchWorkbooks())
      dispatch.current(getSettings([DATA_RANGE_IP]))
      dispatch.current(getICD10Tree())
      dispatch.current(getICD10IPDNTree())
      dispatch.current(getProcedureTree())
      dispatch.current(getProcedureIPDNTree())
      dispatch.current(fetchFields())

      const page = searchParams.get('page')
      switch (page) {
        case 'cohort_builder':
          history.current.push(Routes.Projects)
          break;
        case 'cohort_hru':
          history.current.push(Routes.MyStudiesHRU)
          break;
        case 'cohort_pwv':
          history.current.push(Routes.MyStudiesPWV)
          break;
        case 'glossary':
          history.current.push(Routes.Glossary)
          break;
        case 'training':
          history.current.push(Routes.Training)
          break;
        default:
          const requestURL = searchParams.get('requestURL')
          if (requestURL) {
            history.current.push(requestURL)
          } else if (location.current.pathname === "/") {
            history.current.push(Routes.Home)
          }
          break;
      }
    }
  }, [loggedIn])

  const settingValues = useAppSelector(selectValues)



  return (
    <SettingsContext.Provider value={settingValues}>
      <PageContent isLoggedIn={loggedIn} setLoggedIn={loggedIn => setLoggedIn(loggedIn)}/>
    </SettingsContext.Provider>
  )
}

export default App